import { ToolbarButton } from '@wordpress/components';
import { __ } from '@wordpress/i18n';
import { useSelect } from '@wordpress/data';
import { blockDefault, copy, symbol } from '@wordpress/icons';
import {
	getBlockType,
	getDefaultBlockName,
	store as blocksStore,
} from '@wordpress/blocks';
import { store as preferencesStore } from '@wordpress/preferences';
import BlockSwitcher from '../block-switcher';
import BlockIcon from '../block-icon';
import BlockStylesDropdown from './block-styles-dropdown';
import PatternOverridesDropdown from './pattern-overrides-dropdown';
import useBlockDisplayTitle from '../block-title/use-block-display-title';
import { store as blockEditorStore } from '../../store';
import { hasPatternOverridesDefaultBinding } from '../../utils/block-bindings';
import { unlock } from '../../lock-unlock';

function getBlockIconVariant( { select, clientIds } ) {
	if ( ! clientIds.length ) {
		return 'default';
	}

	const {
		getBlockName,
		getBlockAttributes,
		getBlockParentsByBlockName,
		canRemoveBlocks,
		getTemplateLock,
		getBlockEditingMode,
		canEditBlock,
		isSectionBlock,
	} = unlock( select( blockEditorStore ) );
	const { getBlockStyles } = select( blocksStore );

	const hasTemplateLock = clientIds.some(
		( id ) => getTemplateLock( id ) === 'contentOnly'
	);

	// Calculate props only used in this function
	const isSingleBlock = clientIds.length === 1;
	const blockName = isSingleBlock && getBlockName( clientIds[ 0 ] );
	const hasBlockStyles =
		isSingleBlock && !! getBlockStyles( blockName )?.length;
	const hasPatternNameInSelection = clientIds.some(
		( id ) =>
			!! getBlockAttributes( id )?.metadata?.patternName &&
			isSectionBlock( id )
	);
	const hasPatternOverrides = clientIds.every( ( clientId ) =>
		hasPatternOverridesDefaultBinding(
			getBlockAttributes( clientId )?.metadata?.bindings
		)
	);
	const hasParentPattern = clientIds.every(
		( clientId ) =>
			getBlockParentsByBlockName( clientId, 'core/block', true ).length >
			0
	);
	const canRemove = canRemoveBlocks( clientIds );
	const canEdit = clientIds.every( ( clientId ) => canEditBlock( clientId ) );
	const editingMode = getBlockEditingMode( clientIds[ 0 ] );
	const isDefaultEditingMode = editingMode === 'default';
	const isContentOnlyMode = editingMode === 'contentOnly';
	const _hideTransformsForSections = hasPatternNameInSelection;
	const _showBlockSwitcher =
		! _hideTransformsForSections &&
		isDefaultEditingMode &&
		( hasBlockStyles || canRemove ) &&
		! hasTemplateLock &&
		canEdit;

	const _showPatternOverrides = hasPatternOverrides && hasParentPattern;

	if ( _showBlockSwitcher ) {
		return 'switcher';
	} else if (
		isContentOnlyMode &&
		hasBlockStyles &&
		! hasPatternOverrides &&
		canEdit
	) {
		return 'styles-only';
	} else if ( _showPatternOverrides ) {
		return 'pattern-overrides';
	}

	return 'default';
}

function getBlockIcon( { select, clientIds } ) {
	// Without blocks the icon stands in for the default block, falling back to the generic block icon when no default block type is registered.
	if ( ! clientIds.length ) {
		return getBlockType( getDefaultBlockName() )?.icon ?? blockDefault;
	}

	const { getBlockName, getBlockAttributes, getBlock, isSectionBlock } =
		unlock( select( blockEditorStore ) );

	const _isSingleBlock = clientIds.length === 1;
	const firstClientId = clientIds[ 0 ];

	const blockAttributes = getBlockAttributes( firstClientId );
	if (
		_isSingleBlock &&
		blockAttributes?.metadata?.patternName &&
		isSectionBlock( firstClientId )
	) {
		return symbol;
	}

	const blockName = getBlockName( firstClientId );
	const blockType = getBlockType( blockName );
	if ( _isSingleBlock ) {
		const { getActiveBlockVariation } = select( blocksStore );
		const match = getActiveBlockVariation(
			blockName,
			blockAttributes,
			undefined,
			getBlock?.( firstClientId )?.innerContent
		);
		return match?.icon || blockType?.icon;
	}

	const blockNames = clientIds.map( ( id ) => getBlockName( id ) );
	const isSelectionOfSameType = new Set( blockNames ).size === 1;
	return isSelectionOfSameType ? blockType?.icon : copy;
}

export default function BlockToolbarIcon( { clientIds, isSynced } ) {
	const { icon, showIconLabels, variant } = useSelect(
		( select ) => {
			return {
				icon: getBlockIcon( { select, clientIds } ),
				showIconLabels: select( preferencesStore ).get(
					'core',
					'showIconLabels'
				),
				variant: getBlockIconVariant( {
					select,
					clientIds,
				} ),
			};
		},
		[ clientIds ]
	);

	const blockTitle = useBlockDisplayTitle( {
		clientId: clientIds?.[ 0 ],
		maximumLength: 35,
	} );

	const isSingleBlock = clientIds.length === 1;
	const showBlockTitle = isSingleBlock && isSynced && ! showIconLabels;
	let label = isSingleBlock ? blockTitle : __( 'Multiple blocks selected' );
	if ( ! clientIds.length ) {
		label = getBlockType( getDefaultBlockName() )?.title ?? __( 'Block' );
	}
	// Used to hide the block icon when the showIconLabels preference is enabled, or to display the template title when it's a template.
	const text = showBlockTitle && blockTitle ? blockTitle : undefined;

	const BlockIconElement = (
		<BlockIcon
			className="block-editor-block-toolbar__block-icon"
			icon={ icon }
		/>
	);

	if ( variant === 'switcher' ) {
		return (
			<BlockSwitcher
				clientIds={ clientIds }
				label={ label }
				text={ text }
			>
				{ BlockIconElement }
			</BlockSwitcher>
		);
	}

	if ( variant === 'styles-only' ) {
		return (
			<BlockStylesDropdown
				clientIds={ clientIds }
				label={ label }
				text={ text }
			>
				{ BlockIconElement }
			</BlockStylesDropdown>
		);
	}

	if ( variant === 'pattern-overrides' ) {
		return (
			<PatternOverridesDropdown
				icon={ BlockIconElement }
				clientIds={ clientIds }
				blockTitle={ blockTitle }
				label={ label }
			/>
		);
	}

	return (
		<ToolbarButton
			disabled
			className="block-editor-block-toolbar__block-icon-button"
			title={ label }
			icon={ BlockIconElement }
			text={ text }
		/>
	);
}
